import { createGameSound } from './188-game-sound.mjs';
import { createSkyPlayer } from './110-sky-player.mjs';
import { createSteamPlayer } from './88-steam-player.mjs';
import { paintIconAction } from './82-icon-actions.mjs';
import { createSandConveyor } from './68-sand-conveyor.mjs';
import { createWaterworks } from './69-waterworks.mjs';
import { createOrchardRushPlayer } from './71-orchard-rush.mjs';
import { createBobPlayer } from './76-bob-player.mjs';
import { createGameImmersion } from './46-game-immersion.mjs';
import { shareGame } from './66-game-sharing.mjs';
import { GAME_ENGINE_VERSION } from '../../../packages/domain/src/50-game-engine.mjs';

const factories = {
  'sky-rider': createSkyPlayer,
  'steam-punk-star-killer': createSteamPlayer,
  'bob-bounce': createBobPlayer,
  'sand-layers': createSandConveyor,
  'thread-loom': createWaterworks,
  'ripple-garden': createOrchardRushPlayer,
};

/** Shared native game action keeps the visible label and accessible name aligned. */
export function createGameAction(ctx, text, label, fn, icon = null) {
  const button = ctx.e('button', 'game-action', text);
  button.type = 'button';
  paintIconAction(ctx, button, icon, label, text);
  button.addEventListener('click', fn);
  return button;
}

/** Private arcade companions reuse the card's actor, feature, visibility and
 * disposal boundaries. Their image/physics state never becomes a score transcript,
 * feed acknowledgement or shared URL. Only an explicit share sends the seed. */
export function renderArcadePlayer(ctx, game, { seed, standalone = false, players }) {
  const factory = factories[game.id];
  if (!factory) return null;
  const actor = ctx.state?.me?.id ?? null;
  const host = ctx.e('article', 'game-card game-custom-card');
  host.dataset.gameId = game.id;
  host.dataset.gameKind = game.kind;
  host.dataset.contentKind = 'game';
  host.dataset.tags = game.tags.join(' ');
  const header = ctx.e('header', 'game-heading');
  const titles = ctx.e('div');
  titles.append(
    ctx.e('span', 'game-eyebrow', 'ARCADE · PRIVATE PLAY'),
    ctx.e('h2', '', game.title),
  );
  const body = ctx.e('div', 'game-custom-body');
  const immersion = createGameImmersion(ctx, host, body, { standalone });
  const share = ctx.e('button', 'game-action', '↗');
  share.type = 'button';
  paintIconAction(ctx, share, 'share-2', 'Share this game');
  header.append(titles, share, immersion.button);
  host.append(header, body);
  const unavailable = ctx.e('p', 'game-custom-unavailable', 'This game is currently unavailable.');
  unavailable.hidden = true;
  host.append(unavailable);
  let disposed = false,
    intersecting = standalone,
    latest = null;
  const allowed = () =>
    !disposed &&
    game.available &&
    (ctx.state?.me?.id ?? null) === actor &&
    ctx.state?.features?.games === true &&
    ctx.state.features[`game-${game.id}`] === true;
  const visible = () =>
    host.isConnected &&
    !document.hidden &&
    (standalone || host.dataset.immersive === 'true' || intersecting);
  const sound = !['bob-bounce', 'sky-rider'].includes(game.id)
    ? createGameSound(ctx, {
        controls: header,
        more: share,
        board: body,
        enabled: () => allowed() && visible(),
      })
    : null;
  const controller = factory(ctx, {
    host: body,
    seed,
    allowed,
    visible,
    onCue: (name, settings) => sound?.cue(name, settings),
    onProgress: (value) => {
      sound?.progress(latest, value);
      latest = value;
    },
    onStart: () => {
      for (const other of players) if (other !== player) other.pause();
    },
  });
  let mountFrame = 0,
    mountObserver = null;
  const player = {
    dispose,
    tick: refresh,
    pause: () => {
      controller.pause?.();
      sound?.stop();
    },
  };
  players.add(player);
  function refresh() {
    if (disposed) return;
    const enabled = allowed();
    body.hidden = !enabled;
    unavailable.hidden = enabled;
    share.disabled = !enabled;
    if (!visible() || !enabled) sound?.stop();
    if (!enabled) {
      cancelAnimationFrame(mountFrame);
      mountFrame = 0;
    }
    if (!enabled || !visible()) controller.cancel?.();
    controller.refresh?.();
  }
  const observer = new IntersectionObserver(
    ([entry]) => {
      intersecting = entry.isIntersecting && entry.intersectionRatio >= 0.2;
      refresh();
    },
    { threshold: [0, 0.2, 0.5] },
  );
  observer.observe(host);
  const sharing = () => {
    if (!allowed()) return;
    const description = latest?.completed
      ? `I finished ${game.title}${latest.score ? ` with ${latest.score} local points` : ''}. Try this game with me.`
      : game.description;
    void shareGame(ctx, { ...game, description }, seed, GAME_ENGINE_VERSION);
  };
  share.addEventListener('click', sharing);
  immersion.button.addEventListener('click', refresh);
  document.addEventListener('visibilitychange', refresh);
  function dispose() {
    if (disposed) return;
    sound?.dispose();
    disposed = true;
    observer.disconnect();
    cancelAnimationFrame(mountFrame);
    mountObserver?.disconnect();
    controller.dispose();
    immersion.dispose();
    share.removeEventListener('click', sharing);
    immersion.button.removeEventListener('click', refresh);
    document.removeEventListener('visibilitychange', refresh);
    players.delete(player);
    latest = null;
  }
  host.disposeGame = dispose;
  host.refreshGame = refresh;
  // Factories are constructed before their cards are attached. A one-shot
  // mount wake is independent of intersection threshold transitions.
  mountFrame = requestAnimationFrame(() => {
    mountFrame = 0;
    refresh();
  });
  if (!host.isConnected && typeof MutationObserver === 'function') {
    mountObserver = new MutationObserver(() => {
      if (host.isConnected) {
        mountObserver.disconnect();
        mountObserver = null;
        refresh();
      }
    });
    mountObserver.observe(document.body, { childList: true, subtree: true });
  }
  queueMicrotask(refresh);
  return host;
}
